/**
 * @file usePurchaseHistory.js
 * @description 金币变动记录（签到/打赏/橘子币充值/消费）
 * @module features/user/composables/usePurchaseHistory
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { GOLD_RECHARGE_PAID_EVENT } from '@/shared/utils/authEvents'
import { fetchGoldRecordPageApi } from '@/features/user/api/userApi'

/** 流水 Tab：CLOCK / TIP / RECHARGE / CONSUME。GOLD_RECHARGE 为旧路由别名，请求会归一成 RECHARGE。 */
const GOLD_RECORD_TYPES = ['CLOCK', 'TIP', 'RECHARGE', 'GOLD_RECHARGE', 'CONSUME']
const GOLD_RECHARGE_SOURCE = 'RECHARGE'

export function usePurchaseHistory() {
  const route = useRoute()
  const loading = ref(false)
  const loadingMore = ref(false)
  const recordType = ref('CLOCK')
  const recordTypeTabs = [
    { value: 'CLOCK', label: '签到' },
    { value: 'TIP', label: '打赏' },
    { value: 'RECHARGE', label: '充值' },
    { value: 'CONSUME', label: '消费' },
  ]
  const purchaseRecords = ref([])
  const accumulatedRecords = ref([])
  const pageNum = ref(1)
  const pageSize = ref(10)
  const _pageSizePc = 4
  const _pageSizeMobile = 5
  const total = ref(0)
  const pages = ref(1)
  const queryMonth = ref((() => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
  })())
  const isMobile = ref(false)
  const purchaseSentinel = ref(null)
  let infiniteObserver = null

  const currentPage = computed(() => pageNum.value)
  const totalPages = computed(() => pages.value || 1)
  const displayRecords = computed(() =>
    isMobile.value ? accumulatedRecords.value : purchaseRecords.value
  )
  const recordsNoMore = computed(() => {
    if (!isMobile.value || total.value === 0) return true
    return accumulatedRecords.value.length >= total.value
  })
  const showInfiniteEndTip = computed(() => {
    const ps = pageSize.value
    if (total.value <= ps) return false
    if (loading.value || loadingMore.value) return false
    if (displayRecords.value.length === 0) return false
    if (isMobile.value) {
      return recordsNoMore.value
    }
    return pageNum.value >= pages.value && pages.value >= 1
  })

  function updateMobileAndPageSize() {
    isMobile.value = isMobileLayoutViewport()
    pageSize.value = isMobile.value ? _pageSizeMobile : _pageSizePc
  }

  function unbindInfiniteObserver() {
    if (infiniteObserver) {
      infiniteObserver.disconnect()
      infiniteObserver = null
    }
  }

  function bindInfiniteObserver() {
    unbindInfiniteObserver()
    if (!isMobile.value || typeof IntersectionObserver === 'undefined') return
    const el = purchaseSentinel.value
    if (!el || recordsNoMore.value || loading.value || loadingMore.value) return
    infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          recordsNoMore.value ||
          loadingMore.value ||
          loading.value ||
          !isMobile.value
        ) {
          return
        }
        fetchPurchaseRecords(true)
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    infiniteObserver.observe(el)
  }

  function resolveRequestPageNum(append) {
    if (append) return pageNum.value + 1
    if (isMobile.value) return 1
    return pageNum.value
  }

  function getCurrentMonth() {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    return `${now.getFullYear()}-${month}`
  }

  /**
   * 路由 query.recordType 合法时返回，否则 null
   * @returns {string|null}
   */
  function resolveRecordTypeFromRoute() {
    const raw = String(route.query.recordType || '')
    if (raw === 'GOLD_RECHARGE') return GOLD_RECHARGE_SOURCE
    return GOLD_RECORD_TYPES.includes(raw) ? raw : null
  }

  /**
   * 分页体可能在根上，也可能包在 data 里
   * @param {object} body
   * @param {number} fallbackPageNum
   */
  function unwrapGoldRecordPage(body, fallbackPageNum) {
    const root = body && typeof body === 'object' ? body : {}
    const inner =
      root.data && typeof root.data === 'object' && !Array.isArray(root.data)
        ? root.data
        : root
    const contents = Array.isArray(inner.contents)
      ? inner.contents
      : Array.isArray(root.contents)
        ? root.contents
        : []
    return {
      pageNum: Number(inner.pageNum || root.pageNum || fallbackPageNum || 1),
      pageSize: Number(inner.pageSize || root.pageSize || pageSize.value),
      total: Number(inner.total ?? root.total ?? 0),
      pages: Number(inner.pages || root.pages || 1),
      contents,
    }
  }

  function getTypeLabel(sourceType) {
    const map = {
      CLOCK: '签到',
      SIGN: '签到',
      TIP: '打赏',
      GOLD_RECHARGE: '充值',
      RECHARGE: '充值',
      CONSUME: '消费',
    }
    return map[sourceType] || sourceType || '-'
  }

  function formatAmount(amount) {
    const numeric = Number(amount || 0)
    if (numeric > 0) return `+${numeric}`
    return `${numeric}`
  }

  async function fetchPurchaseRecords(append) {
    const token = JSON.parse(localStorage.getItem('user'))?.token || ''
    if (!token) return
    if (append) {
      if (
        !isMobile.value ||
        loadingMore.value ||
        recordsNoMore.value ||
        accumulatedRecords.value.length >= total.value
      ) {
        return
      }
    }
    const pageToRequest = resolveRequestPageNum(append)
    if (!append) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    try {
      const response = await fetchGoldRecordPageApi({
        pageNum: pageToRequest,
        pageSize: pageSize.value,
        queryMonth: queryMonth.value,
        sourceType: recordType.value,
      })
      const data = response.data || {}
      if (data.code === 200 || data.code === 0) {
        const page = unwrapGoldRecordPage(data, pageToRequest)
        pageNum.value = page.pageNum
        pageSize.value = page.pageSize
        total.value = page.total
        pages.value = page.pages
        const contents = page.contents

        if (!append) {
          purchaseRecords.value = contents
          if (isMobile.value) {
            accumulatedRecords.value = [...contents]
          } else {
            accumulatedRecords.value = []
          }
        } else if (contents.length > 0) {
          accumulatedRecords.value.push(...contents)
        }
      } else {
        purchaseRecords.value = []
        accumulatedRecords.value = []
        total.value = 0
        pages.value = 1
        console.error(
          '获取金币变动记录失败:',
          data.msg || data.errmsg || '未知错误'
        )
      }
    } catch (error) {
      purchaseRecords.value = []
      accumulatedRecords.value = []
      total.value = 0
      pages.value = 1
      console.error('获取金币变动记录异常:', error?.message || error)
    } finally {
      loading.value = false
      loadingMore.value = false
      nextTick(() => bindInfiniteObserver())
    }
  }

  function disabledFutureMonth(date) {
    if (!date) return false
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    return (
      date.getFullYear() > y ||
      (date.getFullYear() === y && date.getMonth() > m)
    )
  }

  async function onQueryMonthChange() {
    if (!queryMonth.value) {
      queryMonth.value = getCurrentMonth()
    }
    pageNum.value = 1
    accumulatedRecords.value = []
    await fetchPurchaseRecords(false)
  }

  async function switchRecordType(type) {
    if (recordType.value === type) return
    recordType.value = type
    pageNum.value = 1
    accumulatedRecords.value = []
    await fetchPurchaseRecords(false)
  }

  /**
   * 充值成功：切到「充值」栏并重拉（不改路由，避免关掉充值窗）
   */
  function onGoldRechargePaid() {
    if (recordType.value !== GOLD_RECHARGE_SOURCE) {
      void switchRecordType(GOLD_RECHARGE_SOURCE)
      return
    }
    pageNum.value = 1
    accumulatedRecords.value = []
    void fetchPurchaseRecords(false)
  }

  watch(
    () => route.query.recordType,
    (nextType) => {
      const raw = String(nextType || '')
      const next = raw === 'GOLD_RECHARGE' ? GOLD_RECHARGE_SOURCE : raw
      if (!['CLOCK', 'TIP', 'RECHARGE', 'CONSUME'].includes(next) || next === recordType.value) return
      void switchRecordType(next)
    }
  )

  async function previousPage() {
    if (isMobile.value) return
    if (pageNum.value <= 1 || loading.value) return
    pageNum.value -= 1
    await fetchPurchaseRecords(false)
  }

  async function nextPage() {
    if (isMobile.value) return
    if (pageNum.value >= totalPages.value || loading.value) return
    pageNum.value += 1
    await fetchPurchaseRecords(false)
  }

  function onViewportResize() {
    const prevMobile = isMobile.value
    updateMobileAndPageSize()
    if (prevMobile !== isMobile.value) {
      pageNum.value = 1
      accumulatedRecords.value = []
      fetchPurchaseRecords(false)
      nextTick(() => bindInfiniteObserver())
    }
  }

  onMounted(() => {
    updateMobileAndPageSize()
    const fromRoute = resolveRecordTypeFromRoute()
    if (fromRoute) {
      recordType.value = fromRoute
    }
    fetchPurchaseRecords(false)
    window.addEventListener('resize', onViewportResize)
    window.addEventListener(GOLD_RECHARGE_PAID_EVENT, onGoldRechargePaid)
    nextTick(() => bindInfiniteObserver())
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onViewportResize)
    window.removeEventListener(GOLD_RECHARGE_PAID_EVENT, onGoldRechargePaid)
    unbindInfiniteObserver()
  })

  return {
    loading,
    loadingMore,
    recordType,
    recordTypeTabs,
    queryMonth,
    isMobile,
    purchaseSentinel,
    currentPage,
    totalPages,
    total,
    displayRecords,
    showInfiniteEndTip,
    getTypeLabel,
    formatAmount,
    disabledFutureMonth,
    onQueryMonthChange,
    switchRecordType,
    previousPage,
    nextPage,
  }
}
